import qapi from "views_shared/app/javascript/api";
import { serializeModel } from "../edit/publications_modal_body/serialization";

// Context-fed action hook for media jobs (publications) — mirrors use_pr_employments_api. The provider
// owns the `mediaJobs` collection (via the invariant-preserving setMediaJobs); this performs writes
// against it (optimistic update + snapshot rollback), routes failures to the shared generalError, and
// reconciles via silentReload. The mediaJobs mutation clients already throw on a non-2xx
// (throwOnApiError), so these try/catch blocks are load-bearing.
const useMediaJobsApi = ({
  mediaProfileId,
  mediaJobs,
  setMediaJobs,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  const addMediaJob = async (mediaJob) => {
    if (isUpdating) return; // guard overlapping mutations
    setGeneralError(null);
    setIsUpdating(true);
    try {
      const { id: _removed, ...serialized } = serializeModel(mediaJob);
      // A reload (not a manual optimistic insert) is simplest here — the new row only has a server id
      // after the create, and silentReload re-orders it correctly via the provider fetch. The first
      // arg is unused by the client (signature parity with createPrProfileJob); the FK travels in
      // `serialized` as media_outlet_id.
      await qapi.mediaJobs.createMediaProfileJob(mediaJob.mediaOutletId, mediaProfileId, serialized);
      await silentReload();
    } catch (e) {
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const removeMediaJob = async (id) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = mediaJobs;

    setMediaJobs((current) => current.filter((j) => j.id !== id)); // optimistic

    setIsUpdating(true);
    try {
      await qapi.mediaJobs.deleteMediaJobById(mediaProfileId, id);
      await silentReload();
    } catch (e) {
      setMediaJobs(snapshot); // roll back
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateMediaJob = async (id, mediaJob) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = mediaJobs;

    setMediaJobs((current) => current.map((j) => (j.id === id ? { ...j, ...mediaJob } : j))); // optimistic

    setIsUpdating(true);
    try {
      const { id: _id, ...attributes } = mediaJob;
      await qapi.mediaJobs.updateByProfileIdAndId(mediaProfileId, id, serializeModel(attributes));
      await silentReload();
    } catch (e) {
      setMediaJobs(snapshot); // roll back
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  return { addMediaJob, removeMediaJob, updateMediaJob };
};

export default useMediaJobsApi;
